import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import type { UserRole } from '@/types'

interface ProtectedRouteProps {
  /**
   * Required role(s) for accessing the route.
   * - string: exact role match
   * - string[]: any of these roles is allowed
   * - undefined: any authenticated user
   */
  requiredRole?: UserRole | UserRole[]
  /**
   * Where to redirect if user is NOT authenticated. Default: '/login'
   * For resident scope, pass '/portal/login'.
   */
  unauthenticatedRedirect?: string
  /**
   * Where to redirect if user IS authenticated but lacks the required role.
   * Default: decide based on user's role (admin/superuser → '/dashboard',
   * resident → '/portal/dashboard').
   */
  forbiddenRedirect?: string
  children: ReactNode
}

/**
 * Derive the effective UserRole from the User object.
 * Prefers explicit `role` field; falls back to is_superuser/is_active heuristic
 * for backward compatibility during backend rollout.
 */
function deriveRole(user: { role?: UserRole; is_superuser?: boolean } | null): UserRole | null {
  if (!user) return null
  if (user.role) return user.role
  // Legacy fallback: if is_superuser → superuser, else assume admin
  // (residents MUST come from backend with explicit role === 'resident').
  if (user.is_superuser) return 'superuser'
  return 'admin'
}

/**
 * Route guard enforcing authentication and (optionally) role membership.
 * - Not authenticated → redirect to `unauthenticatedRedirect`
 * - Authenticated but wrong role → redirect to `forbiddenRedirect`
 * - Authenticated with correct role → render children
 */
export default function ProtectedRoute({
  requiredRole,
  unauthenticatedRedirect = '/login',
  forbiddenRedirect,
  children,
}: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const location = useLocation()

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={unauthenticatedRedirect}
        replace
        state={{ from: location.pathname }}
      />
    )
  }

  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole]
    const role = deriveRole(user)
    if (!role || !allowed.includes(role)) {
      // Default redirect target based on user's actual role.
      const defaultTarget =
        role === 'resident' ? '/portal/dashboard' : '/dashboard'
      return <Navigate to={forbiddenRedirect ?? defaultTarget} replace />
    }
  }

  return <>{children}</>
}

export { deriveRole }
